import { Action } from 'store/store';
import omitUndefined from 'utils/omitUndefined';
import { WidgetTypeActionPayload, WidgetActionType } from '../actions';
import { WidgetType } from '../../WidgetType';
import { StreamError } from 'streamsState/store/streamStatuses/StreamErrorState';

export interface WidgetStreamActionPayload extends WidgetTypeActionPayload {
    streamKey: string;
    allowMultiStream?: boolean;
}

export interface WidgetStreamMessageReceivePayload<T>
    extends WidgetStreamActionPayload {
    message: T;
}

export interface WidgetStreamMessageReceiveBatchPayload<T>
    extends WidgetStreamActionPayload {
    messages: T[];
}

export type WidgetStreamMessageReceiveAction<T> = Action<
    WidgetActionType.WIDGET_RECEIVE_STREAM_MESSAGE,
    WidgetStreamMessageReceivePayload<T>
>;

export function createReceiveMessageAction<T>(widgetType: WidgetType) {
    return (
        widgetKey: string,
        streamKey: string,
        message: T,
        allowMultiStream?: boolean
    ): WidgetStreamMessageReceiveAction<T> => {
        if (!widgetType || !widgetKey) {
            throw new Error('No widget type or widget key received');
        }
        return {
            type: WidgetActionType.WIDGET_RECEIVE_STREAM_MESSAGE,
            payload: omitUndefined({
                widgetType,
                widgetKey,
                streamKey,
                message,
                allowMultiStream,
            }),
        };
    };
}

export type WidgetStreamMessageReceiveBatchAction<T> = Action<
    WidgetActionType.WIDGET_RECEIVE_STREAM_MESSAGE_BATCH,
    WidgetStreamMessageReceiveBatchPayload<T>
>;

export function createReceiveMessageBatchAction<T>(widgetType: WidgetType) {
    return (
        widgetKey: string,
        streamKey: string,
        messages: T[],
        allowMultiStream?: boolean
    ): WidgetStreamMessageReceiveBatchAction<T> => ({
        type: WidgetActionType.WIDGET_RECEIVE_STREAM_MESSAGE_BATCH,
        payload: omitUndefined({
            widgetType,
            widgetKey,
            streamKey,
            messages,
            allowMultiStream,
        }),
    });
}

export type WidgetClearStreamDataPayload = WidgetTypeActionPayload;
export type WidgetClearStreamDataAction = Action<
    WidgetActionType.WIDGET_CLEAR_STREAM_DATA,
    WidgetClearStreamDataPayload
>;

export function createClearStreamDataAction(widgetType: WidgetType) {
    return (widgetKey: string): WidgetClearStreamDataAction => ({
        type: WidgetActionType.WIDGET_CLEAR_STREAM_DATA,
        payload: {
            widgetType,
            widgetKey,
        },
    });
}

export interface WidgetSetStreamDataPayload extends WidgetTypeActionPayload {
    data: unknown;
}

export type WidgetSetStreamDataAction = Action<
    WidgetActionType.WIDGET_SET_STREAM_DATA,
    WidgetSetStreamDataPayload
>;

/*
 * Replaces the stream data completely. You will not normally use this. It was added for the support visualisation tool
 */
export function createSetStreamDataAction(widgetType: WidgetType) {
    return (widgetKey: string, data: unknown): WidgetSetStreamDataAction => ({
        type: WidgetActionType.WIDGET_SET_STREAM_DATA,
        payload: {
            widgetType,
            widgetKey,
            data,
        },
    });
}

export type WidgetWatchStreamAction = Action<
    WidgetActionType.WIDGET_WATCH_STREAM,
    WidgetStreamActionPayload
>;

export function createWatchStreamAction(widgetType: WidgetType) {
    return (
        widgetKey: string,
        streamKey: string,
        allowMultiStream?: boolean
    ): WidgetWatchStreamAction => ({
        type: WidgetActionType.WIDGET_WATCH_STREAM,
        payload: omitUndefined({
            widgetType,
            widgetKey,
            streamKey,
            allowMultiStream,
        }),
    });
}

export interface WidgetStreamHandleStreamErrorActionPayload
    extends WidgetStreamActionPayload {
    error: StreamError;
}

export type WidgetStreamHandleStreamErrorAction = Action<
    WidgetActionType.WIDGET_HANDLE_STREAM_ERROR,
    WidgetStreamHandleStreamErrorActionPayload
>;

export function createWidgetHandleStreamErrorAction(widgetType: WidgetType) {
    return (
        widgetKey: string,
        streamKey: string,
        error: StreamError
    ): WidgetStreamHandleStreamErrorAction => ({
        type: WidgetActionType.WIDGET_HANDLE_STREAM_ERROR,
        payload: omitUndefined({
            widgetType,
            widgetKey,
            streamKey,
            error,
        }),
    });
}

export type WidgetStreamAction<T> =
    | WidgetWatchStreamAction
    | WidgetClearStreamDataAction
    | WidgetSetStreamDataAction
    | WidgetStreamMessageReceiveAction<T>
    | WidgetStreamMessageReceiveBatchAction<T>
    | WidgetStreamHandleStreamErrorAction;
